import React, { useState } from 'react';

import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import { PAGES } from '../../constants/pages';
import { AiOutlineInfoCircle } from 'react-icons/ai';

import {
    MicrostoreElementState,
    StockVariationType,
    useCreateStockVariationMutation,
    TotadminPermission,
} from 'data/__generated__';

import { Footer } from 'components/Footer';
import { Header, HeaderTitle } from 'components/Header';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { TotemSelect } from 'components/TotemSelect';
import { TotemInput } from 'components/TotemInput';
import { PageTitle } from 'components/PageTitle';
import { Card, CardContent } from 'components/ui/card';
import { ColumnSelect } from 'components/Select/ColumnSelect';
import { ProductWithStocksSelector } from './components/ProductWithStocksSelector';
import { stockVariationTypeIsNegativeVariation, siteVariationsLabels } from './constants';
import { PermissionGate } from 'components/PermissionGate';

type FormType = {
    columnId: string | null;
    productId: string | null;
    productLabel: string | null;
    delta: number;
    explanation: string | null;
    typeValue: StockVariationType | null;
    typeLabel: string | null;
};

const initialFormData: FormType = {
    columnId: null,
    productId: null,
    productLabel: null,
    delta: 0,
    explanation: null,
    typeValue: null,
    typeLabel: null,
};

const SELECTABLE_COLUMN_STATES = [MicrostoreElementState.Regular];

export const StockVariationCreate = () => {
    const [formData, setFormData] = useState<FormType>(initialFormData);
    const [createStockVariation] = useCreateStockVariationMutation();

    const typeOptions = Object.values(StockVariationType).map((s) => ({
        label: `${siteVariationsLabels[s]} (${s})`,
        value: s,
    }));

    const handleColumnSelect = (columnId: string | null) => {
        setFormData({ ...formData, columnId, productId: null, productLabel: null });
    };

    const handleProductSelect = (productId: string, productLabel: string) => {
        setFormData({ ...formData, productId, productLabel });
    };

    const handleTypeSelect = (typeValue: StockVariationType | null) => {
        if (!typeValue) return;

        const isNegativeDelta = stockVariationTypeIsNegativeVariation[typeValue];
        const shouldFlipSign = (isNegativeDelta && formData.delta > 0) || (!isNegativeDelta && formData.delta < 0);
        const delta = shouldFlipSign ? -formData.delta : formData.delta;
        const typeLabel = typeOptions.find((o) => o.value === typeValue)?.label ?? null;

        setFormData({ ...formData, typeValue, typeLabel, delta });
    };

    const handleDeltaInput = (value: string) => {
        const numberValue = Number(value);
        const isNegativeDelta = formData.typeValue && stockVariationTypeIsNegativeVariation[formData.typeValue];

        if ((isNegativeDelta && numberValue > 0) || (!isNegativeDelta && numberValue < 0)) {
            return;
        }

        setFormData({ ...formData, delta: numberValue });
    };

    const handleExplanationInput = (value: string) => {
        setFormData({ ...formData, explanation: value });
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const { columnId, productId, productLabel, delta, explanation, typeValue, typeLabel } = formData;
        if (!explanation || !columnId || !productId || delta === 0 || !typeValue) {
            toast.error("L'un des champs n'a pas été renseigné");
            return;
        }
        const result = await createStockVariation({
            variables: { columnId, productId, delta, explanation, type: typeValue },
        });

        const error = result.data?.createStockVariation.error;
        if (error) {
            throw new Error(`L'erreur suivante est survenue : ${error}`);
        }

        setFormData(initialFormData);
        toast.success(`La variation ${typeLabel} a été appliquée au produit "${productLabel}".`);
    };

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.stockVariationCreate} />
                </HeaderTitle>
            </Header>
            <Content>
                <Form onSubmit={handleSubmit}>
                    <ContentScrollable>
                        <Card className="bg-white">
                            <CardContent>
                                <Fields>
                                    <Info>
                                        <AiOutlineInfoCircle /> Une variation de stock est un changement particulier de
                                        stock existant (ex: si le stock est de 50 et que l'on achète un coca (variation
                                        de stock = 1), le nouveau stock est 49). Pour modifier le stock absolu (ex: 45
                                        au lieu de 50):{' '}
                                        <Link to="/site-inventory-stock/create">/site-inventory-stock/create</Link>
                                    </Info>

                                    <ColumnSelect
                                        label="Colonne"
                                        value={formData.columnId}
                                        onChange={handleColumnSelect}
                                        isClearable
                                        dataTest="column-selector"
                                        states={SELECTABLE_COLUMN_STATES}
                                    />

                                    {formData.columnId ? (
                                        <PermissionGate
                                            requiredPermissions={[TotadminPermission.MicrostoreColumnsRead]}
                                        >
                                            <ProductWithStocksSelector
                                                productOption={formData.productId}
                                                handleProductSelect={handleProductSelect}
                                                columnId={formData.columnId}
                                            />
                                        </PermissionGate>
                                    ) : null}

                                    <TotemSelect
                                        label="Choisir le type de variation de stock"
                                        placeholder="Sélectionner un type"
                                        value={formData.typeValue}
                                        options={typeOptions}
                                        onChange={handleTypeSelect}
                                        dataTest="type-selector"
                                    />

                                    {formData.typeValue ? (
                                        <TotemInput
                                            label="Quantité"
                                            placeholder="Renseigner le nombre d'unités du produit"
                                            type="number"
                                            value={formData.delta ?? 0}
                                            onChange={handleDeltaInput}
                                            data-test="product-delta"
                                        />
                                    ) : null}

                                    {formData.typeValue ? (
                                        <TotemInput
                                            label="Pourquoi souhaitez-vous modifier le stock ?"
                                            placeholder="Renseigner la raison de la modification de stock"
                                            type="string"
                                            value={formData.explanation ?? ''}
                                            onChange={handleExplanationInput}
                                            data-test="stock-variation-explanation"
                                        />
                                    ) : null}
                                </Fields>
                            </CardContent>
                        </Card>
                    </ContentScrollable>
                    <Footer>
                        <TotemPrimaryButton
                            data-test="stock-variation-create_submit-button"
                            requiredPermissions={[TotadminPermission.StockVariationsManage]}
                        >
                            Ajouter la variation de stock
                        </TotemPrimaryButton>
                    </Footer>
                </Form>
            </Content>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
`;

const Form = styled.form`
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const ContentScrollable = styled.div`
    padding: 15px;
    width: 100%;
    flex: 1;
    overflow-y: auto;
`;

const Fields = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 15px;
`;

const Info = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${({ theme }) => theme.infoTextColor};
    font-size: 14px;
`;
